// Shared ProV4 field helpers: show/hide fields, "2 Stacked Numbers" / "2 Separate Names" / "Custom ... Size"
// toggles, and the inline "Please Enter ..." errors that block Add to Cart.
// Used by prov4-customizations.js (number kits) and prov4-addnum.js (Additional Name/Number).
window.Prov4Fields = window.Prov4Fields || {
  // A required pill group's radios share one error via data-error-id
  setFieldError(input, show) {
    const error = document.getElementById(input.dataset.errorId || `${input.id}-error`);
    if (error) error.hidden = !show;
    if (show) {
      input.setAttribute('aria-invalid', 'true');
    } else {
      input.removeAttribute('aria-invalid');
    }
  },

  // Enabled inputs are sent with the order; disabled ones are not. Hidden fields are cleared.
  setFieldEnabled(input, enabled, required = false) {
    if (!input) return;
    input.disabled = !enabled;
    input.required = enabled && required;
    if (!enabled) {
      input.value = '';
      this.setFieldError(input, false);
      const unknownToggle = document.querySelector(`[data-unknown-toggle="${input.id}"]`);
      if (unknownToggle && unknownToggle.checked) {
        unknownToggle.checked = false;
        input.readOnly = false;
      }
    }
  },

  // "Don't know? I can look it up for you.": the field saves "Lookup" (read-only, so it passes the required check)
  bindUnknownToggles(root) {
    root.querySelectorAll('[data-unknown-toggle]').forEach(toggle => {
      toggle.addEventListener('change', () => {
        const input = document.getElementById(toggle.dataset.unknownToggle);
        if (!input) return;
        input.readOnly = toggle.checked;
        input.value = toggle.checked ? 'Lookup' : '';
        if (toggle.checked) this.setFieldError(input, false);
      });
    });
  },

  toggleSecondField(toggle) {
    const secondField = document.getElementById(toggle.dataset.secondFieldToggle);
    if (!secondField) return;
    secondField.style.display = toggle.checked ? '' : 'none';
    this.setFieldEnabled(secondField.querySelector('input'), toggle.checked, true);
    if ('sizeToggle' in toggle.dataset) this.updateDecalSizing(toggle.closest('[data-sizing-root]'));
  },

  // The kit's Decal Sizing (prov4-customization-selector.liquid) saves Custom Size while any "Custom ... Size" box is checked
  updateDecalSizing(root) {
    const sizing = root && root.querySelector('[data-decal-sizing]');
    if (!sizing) return;
    sizing.value = root.querySelector('[data-size-toggle]:checked') ? 'Custom Size' : sizing.dataset.decalSizing;
  },

  // Untick and hide a second field (e.g. when its parent field is hidden)
  resetSecondField(toggle) {
    if (toggle.checked) {
      toggle.checked = false;
      this.toggleSecondField(toggle);
    }
  },

  bindSecondFieldToggles(root) {
    root.querySelectorAll('[data-second-field-toggle]').forEach(toggle => {
      toggle.addEventListener('change', () => this.toggleSecondField(toggle));
    });
  },

  // Dawn's product form is novalidate, so `required` alone never blocks Add to Cart.
  // Check root's required fields first (capture phase runs before product-form.js).
  bindRequiredCheck(root) {
    document.addEventListener('submit', (e) => {
      const invalid = Array.from(root.querySelectorAll('input[required]')).filter(
        (input, index, all) => !input.disabled && input.form === e.target && !input.checkValidity() &&
          // One entry per pill group
          (input.type !== 'radio' || all.findIndex(other => other.name === input.name) === index)
      );
      if (invalid.length) {
        e.preventDefault();
        e.stopImmediatePropagation();
        invalid.forEach(input => this.setFieldError(input, true));
        invalid[0].focus();
        invalid[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, true);

    // Clear a field's error as soon as the customer types something in it
    root.addEventListener('input', (e) => {
      if (e.target.matches('input[required]') && e.target.checkValidity()) {
        this.setFieldError(e.target, false);
      }
    });
  }
};

// Edit from cart: the cart's Edit button (main-cart-items.liquid) opens a ProV4 page with ?edit=<line key>.
// The form is filled from that cart line, the button becomes "Update Cart", and Update swaps the line.
window.Prov4Edit = window.Prov4Edit || {
  async loadItem() {
    const key = new URLSearchParams(window.location.search).get('edit');
    if (!key) return null;
    try {
      const cart = await fetch(`${window.routes.cart_url}.js`).then(response => response.json());
      return cart.items.find(item => item.key === key) || null;
    } catch (e) {
      return null;
    }
  },

  // Call after the page's own field setup and Prov4Fields.bindRequiredCheck(root)
  start(root, item, sectionId) {
    this.fill(root, item.properties || {});
    const quantityInput = document.getElementById(`Quantity-${sectionId}`);
    if (quantityInput) quantityInput.value = item.quantity;

    const banner = document.getElementById(`prov4-edit-banner-${sectionId}`);
    if (banner) banner.hidden = false;
    const form = root.querySelector('[form]').form;
    this.setButtonText(form);
    this.bindUpdate(form, item, document.getElementById(`prov4-edit-error-${sectionId}`));
  },

  // Fills by saved field name. Pills first (they reveal fields), then "2 ..." and "don't have my order number"
  // checkboxes, then text. Kit swatches are set by prov4-customizations.js before this runs.
  fill(root, props) {
    const pick = (radios, value) => {
      const radio = radios.find(r => r.value === value);
      if (!radio) return;
      radio.checked = true;
      radio.dispatchEvent(new Event('input', { bubbles: true }));
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    };
    const propertyName = input => (input.name.match(/^properties\[(.*)\]$/) || [])[1];

    root.querySelectorAll('fieldset').forEach(fieldset => {
      const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      if (!radios.length) return;
      // Pills save either through their own name or a hidden input in the same fieldset
      const named = propertyName(radios[0]);
      const hidden = fieldset.querySelector('input[type="hidden"][name^="properties["]');
      const name = named || (hidden && propertyName(hidden));
      if (name && props[name]) pick(radios, props[name]);
    });

    root.querySelectorAll('[data-second-field-toggle]').forEach(toggle => {
      const secondInput = document.querySelector(`#${toggle.dataset.secondFieldToggle} input`);
      if (!secondInput || !props[propertyName(secondInput)] || toggle.closest('[style*="display: none"]')) return;
      toggle.checked = true;
      Prov4Fields.toggleSecondField(toggle);
    });

    root.querySelectorAll('[data-unknown-toggle]').forEach(toggle => {
      const input = document.getElementById(toggle.dataset.unknownToggle);
      // "Unknown" is what cart lines added before the "Lookup" wording saved
      if (input && !input.disabled && ['Lookup', 'Unknown'].includes(props[propertyName(input)])) {
        toggle.checked = true;
        toggle.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    root.querySelectorAll('input[type="text"][name^="properties["]').forEach(input => {
      const value = props[propertyName(input)];
      if (value && !input.disabled && !input.readOnly) input.value = value;
    });
  },

  setButtonText(form) {
    const label = form.querySelector('[type="submit"] span');
    if (!label) return;
    label.textContent = 'Update Cart';
    // Dawn puts "Add to cart" back after a variant change; switch it to "Update Cart" again
    new MutationObserver(() => {
      if (label.textContent.trim() === window.variantStrings.addToCart) label.textContent = 'Update Cart';
    }).observe(label, { childList: true, characterData: true, subtree: true });
  },

  // Runs after Prov4Fields' required check (both are capture listeners; that one is registered first).
  // Adds the edited version, then removes the old line, so the original stays if the add fails.
  bindUpdate(form, item, errorBox) {
    const changeLine = (key, quantity) => fetch(`${window.routes.cart_change_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    }).then(response => response.json());

    document.addEventListener('submit', async (e) => {
      if (e.target !== form) return;
      e.preventDefault();
      e.stopImmediatePropagation();

      const button = form.querySelector('[type="submit"]');
      const spinner = button.querySelector('.loading__spinner');
      const setLoading = (loading) => {
        if (loading) {
          button.setAttribute('aria-disabled', 'true');
        } else {
          button.removeAttribute('aria-disabled');
        }
        button.classList.toggle('loading', loading);
        if (spinner) spinner.classList.toggle('hidden', !loading);
      };
      if (button.getAttribute('aria-disabled') === 'true') return;
      setLoading(true);
      if (errorBox) errorBox.hidden = true;

      const formData = new FormData(form);
      // Keep hidden link properties (_Kit ID, _Rides With Kit) when a line is edited from the cart
      Object.entries(item.properties || {}).forEach(([name, value]) => {
        if (name.startsWith('_') && !formData.has(`properties[${name}]`)) formData.append(`properties[${name}]`, value);
      });
      const quantity = Number(formData.get('quantity')) || item.quantity;
      try {
        const added = await fetch(`${window.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData
        }).then(response => response.json());
        if (added.status) throw new Error(added.description || added.message);

        // Nothing changed: Shopify merged it back into the same line, so just set the quantity
        await changeLine(item.key, added.key === item.key ? quantity : 0);
        window.location = window.routes.cart_url;
      } catch (err) {
        if (errorBox) {
          errorBox.querySelector('[data-edit-error-text]').textContent =
            `Couldn't update your cart item. ${err.message || 'Please try again.'}`;
          errorBox.hidden = false;
        }
        setLoading(false);
      }
    }, true);
  }
};

// Wrapper for pages that only need required-field checks and Edit from cart (e.g. Heart Graphic names)
if (!customElements.get('prov4-required-fields')) {
  customElements.define('prov4-required-fields', class extends HTMLElement {
    connectedCallback() {
      Prov4Fields.bindRequiredCheck(this);
      Prov4Edit.loadItem().then(item => {
        if (item) Prov4Edit.start(this, item, this.dataset.sectionId);
      });
    }
  });
}
